// FeeFormModal — "Add New Fee" modal extracted from Fees.tsx (Sprint 5c).
//
// Stateless apart from the local Antd Form instance and the
// condo-wide-vs-individual switch. Receives `owners` for the dropdown,
// `open`, `onCancel`, `onSubmit`. The orchestrator owns the submit handler
// (it knows about currentCondo, the store action, the toast, the modal
// open/close state).
import React, { useState } from 'react';
import { Button, Col, Divider, Form, Input, Modal, Row, Select, Space, Switch } from 'antd';
import { ApartmentOutlined } from '@ant-design/icons';
import { Owner } from '../../types';

const { Option } = Select;

export interface FeeFormValues {
  isCondoWide: boolean;
  ownerId?:    string;
  type:        string;
  amount:      string;   // raw input value; orchestrator parseFloats
  dueDate:     string;   // raw input value; orchestrator new Date()s
  description: string;
}

export interface FeeFormModalProps {
  open: boolean;
  owners: Owner[];
  onCancel: () => void;
  onSubmit: (values: FeeFormValues) => Promise<void> | void;
}

export const FeeFormModal: React.FC<FeeFormModalProps> = ({ open, owners, onCancel, onSubmit }) => {
  const [form] = Form.useForm();
  const [feeType, setFeeType] = useState<'condo-wide' | 'individual'>('condo-wide');

  // Sprint 120 (ADR-113) — the condo-wide fan-out (FeeService) bills one row
  // per ACTIVE owner; an inactive owner is not charged. Count the same set the
  // backend uses so the "applied to all N units" label is truthful. `status`
  // is the Owner field ('active' | 'inactive' | 'pending'); treat a missing
  // status as active (legacy rows) to avoid under-counting.
  const billableOwnerCount = owners.filter((o) => o.status !== 'inactive').length;

  const reset = () => {
    form.resetFields();
    setFeeType('condo-wide');
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const handleFinish = async (values: FeeFormValues) => {
    await onSubmit(values);
    reset();
  };

  return (
    <Modal
      title="Add New Fee"
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{ isCondoWide: true }}
      >
        <Form.Item name="isCondoWide" label="Fee Type">
          <div className="flex items-center space-x-4">
            <span className={feeType === 'condo-wide' ? 'font-medium' : ''}>Condo-wide</span>
            <Switch
              checked={feeType === 'individual'}
              onChange={(checked) => {
                setFeeType(checked ? 'individual' : 'condo-wide');
                form.setFieldsValue({ isCondoWide: checked });
                if (!checked) form.setFieldsValue({ ownerId: undefined });
              }}
              checkedChildren="Individual"
              unCheckedChildren="Condo-wide"
            />
            <span className={feeType === 'individual' ? 'font-medium' : ''}>Individual</span>
          </div>
        </Form.Item>

        <Divider />

        {feeType === 'individual' && (
          <Form.Item
            name="ownerId"
            label="Select Unit / Owner"
            rules={[{ required: true, message: 'Please select owner' }]}
          >
            <Select placeholder="Select owner">
              {owners.map((owner) => (
                <Option key={owner.id} value={owner.userId ?? owner.id}>
                  {owner.name} (Unit {owner.unitNumber})
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {feeType === 'condo-wide' && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <Space>
              <ApartmentOutlined className="text-blue-500" />
              <span className="text-blue-700">
                {/* Sprint 120 (ADR-113) — truthful reach. The condo-wide fan-out
                    bills one row per ACTIVE owner (every owner now has a login,
                    so each is billable). Drive the promised count from the SAME
                    active-owner set so the label never overstates "all N units".
                    `billableOwnerCount` is computed below. */}
                This fee will be applied to all {billableOwnerCount} active unit
                {billableOwnerCount === 1 ? '' : 's'} in the condo
              </span>
            </Space>
          </div>
        )}

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="type"
              label="Fee Type"
              rules={[{ required: true, message: 'Please select fee type' }]}
            >
              <Select placeholder="Select fee type">
                <Option value="MAINTENANCE">Maintenance</Option>
                <Option value="SPECIAL_ASSESSMENT">Special Assessment</Option>
                <Option value="LATE_FEE">Late Fee</Option>
                <Option value="OTHER">Other</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="amount"
              label="Amount"
              rules={[{ required: true, message: 'Please enter amount' }]}
            >
              <Input type="number" placeholder="Enter amount" min={0} step={0.01} prefix="$" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="dueDate"
          label="Due Date"
          rules={[{ required: true, message: 'Please select due date' }]}
        >
          <Input type="date" className="w-full" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true, message: 'Please enter description' }]}
        >
          <Input.TextArea placeholder="Enter fee description" rows={3} />
        </Form.Item>

        <Form.Item className="mb-0">
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Add {feeType === 'condo-wide' ? 'Condo-wide' : 'Individual'} Fee
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};
